import React from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

type Props = {
  children: React.ReactNode;
};

type State = {
  hasError: boolean;
  error?: Error;
  componentStack?: string;
  copied?: boolean;
};

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    console.error("[AppErrorBoundary] Uncaught error:", error);
    console.error("[AppErrorBoundary] Component stack:", info.componentStack);

    // Capture stack for on-screen debugging (useful on native where console is harder to access)
    this.setState({ componentStack: info.componentStack });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private buildCopyText = () => {
    return [
      this.state.error?.message ? `Error: ${this.state.error.message}` : "Error: (no message)",
      this.state.error?.stack ? `\nStack:\n${this.state.error.stack}` : "",
      this.state.componentStack ? `\nComponent stack:\n${this.state.componentStack}` : "",
    ]
      .filter(Boolean)
      .join("\n");
  };

  private fallbackCopy = (text: string) => {
    const el = document.createElement("textarea");
    el.value = text;
    el.setAttribute("readonly", "true");
    el.style.position = "fixed";
    el.style.left = "-9999px";
    el.style.top = "0";
    document.body.appendChild(el);
    el.focus();
    el.select();

    const ok = document.execCommand?.("copy") ?? false;
    document.body.removeChild(el);
    return ok;
  };

  private handleCopyDetails = async () => {
    const details = this.buildCopyText();

    let copied = false;

    // Modern clipboard API (may be blocked in some WebViews)
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(details);
        copied = true;
      }
    } catch {
      // ignore
    }

    // Fallback for Android WebView
    if (!copied) {
      try {
        copied = this.fallbackCopy(details);
      } catch {
        copied = false;
      }
    }

    this.setState({ copied });
    if (copied) {
      setTimeout(() => this.setState({ copied: false }), 1500);
    }
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md p-6 space-y-4">
          <h1 className="text-xl font-semibold">Something went wrong</h1>
          <p className="text-sm text-muted-foreground">
            The app hit an unexpected error after login. Tap reload to try again.
          </p>

          {this.state.error?.message ? (
            <pre className="text-xs whitespace-pre-wrap bg-muted p-3 rounded-md overflow-auto max-h-48">
              {this.state.error.message}
            </pre>
          ) : null}

          {this.state.componentStack ? (
            <details className="text-xs">
              <summary className="cursor-pointer text-muted-foreground">Show component stack</summary>
              <pre className="mt-2 whitespace-pre-wrap bg-muted p-3 rounded-md overflow-auto max-h-48">
                {this.state.componentStack}
              </pre>
            </details>
          ) : null}

          <div className="flex gap-2">
            <Button onClick={this.handleReload} className="flex-1">
              Reload
            </Button>
            <Button variant="outline" onClick={this.handleCopyDetails}>
              {this.state.copied ? "Copied" : "Copy"}
            </Button>
          </div>
        </Card>
      </div>
    );
  }
}
